'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["messageId", "isGrouped", "slots", "slotProps", "children", "groupAuthorName"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import useForkRef from '@mui/utils/useForkRef';
import useEnhancedEffect from '@mui/utils/useEnhancedEffect';
import { useMessage } from "../hooks/useMessage.js";
import { useMessageAuthor } from "../hooks/useMessageAuthor.js";
import { useChatVariant } from "../chat/internals/ChatVariantContext.js";
import { useChatDensity } from "../chat/internals/ChatDensityContext.js";
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { mergeReactProps } from "../internals/mergeReactProps.js";
import { focusFirstFocusableDescendant, useMessageRovingContext, useMessageRovingItem } from "../message-list/internals/MessageRovingContext.js";
import { MessageContextProvider } from "./internals/MessageContext.js";
import { jsx as _jsx } from "react/jsx-runtime";
export const MessageRoot = /*#__PURE__*/React.forwardRef(function MessageRoot(props, ref) {
  const {
      messageId,
      isGrouped = false,
      slots,
      slotProps,
      children,
      groupAuthorName
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  // Dropped on purpose: `groupAuthorName` is a private MessageGroup→ChatMessage
  // channel. A headless `MessageRoot` composition owns its own layout, so we must
  // not forward this non-DOM prop to the root element (it would otherwise leak as
  // an unknown DOM attribute). Pulling it out of `...other` is the whole point.
  void groupAuthorName;
  const message = useMessage(messageId);
  const resolvedAuthor = useMessageAuthor(messageId);
  const variant = useChatVariant();
  const density = useChatDensity();
  const localeText = useChatLocaleText();
  const ownerState = React.useMemo(() => ({
    messageId,
    message,
    role: message?.role,
    status: message?.status,
    streaming: message?.status === 'streaming',
    error: message?.status === 'error',
    isGrouped,
    variant,
    density,
    resolvedAuthor,
    showAvatar: resolvedAuthor?.avatarUrl != null,
    isOwnMessage: resolvedAuthor?.isOwnMessage ?? message?.role === 'user'
  }), [density, isGrouped, message, messageId, resolvedAuthor, variant]);
  // Roving focus: inside a `MessageListRoot` with roving enabled, the article
  // is the list's roving tabindex item (the whole list is a single Tab stop).
  // Outside a roving list the context is null and the article stays a plain,
  // non-focusable container.
  const roving = useMessageRovingContext();
  const rovingItem = useMessageRovingItem(messageId);
  const localRootRef = React.useRef(null);
  const registerRovingRef = React.useCallback(element => {
    localRootRef.current = element;
    roving?.registerItemRef(messageId, element);
  }, [messageId, roving]);
  const handleRef = useForkRef(ref, registerRovingRef);

  // Entering drill-in (Enter on the focused article) moves focus to the first
  // focusable descendant once the actionable re-render has restored the
  // interior controls' tab order. Skipped when focus is already inside (e.g.
  // the user clicked a control directly).
  useEnhancedEffect(() => {
    if (!rovingItem.actionable) {
      return;
    }
    const article = localRootRef.current;
    if (article == null) {
      return;
    }
    if (article.contains(document.activeElement) && document.activeElement !== article) {
      return;
    }
    focusFirstFocusableDescendant(article);
  }, [rovingItem.actionable]);
  const Root = slots?.root ?? 'div';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref: handleRef,
      // `article` is the correct landmark for a self-contained unit of content
      // (each chat message is a discrete piece of content in a feed).
      // Screen readers announce "article" when entering, which helps users
      // understand the structural boundary between messages.
      role: 'article',
      'aria-label': resolvedAuthor?.displayName ? `Message from ${resolvedAuthor.displayName}` : localeText.messageLabel,
      // Hint assistive tech to defer announcing the streaming message until
      // it completes (best effort; the message list's status announcer
      // carries the explicit streaming announcements).
      'aria-busy': ownerState.streaming || undefined
    }, rovingItem.enabled && {
      tabIndex: rovingItem.focused ? 0 : -1,
      'data-actionable': rovingItem.actionable ? 'true' : undefined
    }, getDataAttributes({
      role: ownerState.role,
      status: ownerState.status,
      streaming: ownerState.streaming,
      error: ownerState.error,
      isGrouped: ownerState.isGrouped,
      isOwnMessage: ownerState.isOwnMessage
    }))
  });

  // The roving handlers are merged on top of the resolved slot props so
  // consumer-provided handlers (via `slotProps.root` or forwarded props) keep
  // running — `useSlotProps` alone would let one silently drop the other.
  const composedRootProps = roving == null ? rootProps : mergeReactProps(rootProps, {
    onFocus: () => {
      roving.onItemFocus(messageId);
    },
    onKeyDown: event => {
      roving.onItemKeyDown(event, messageId);
    },
    onBlur: event => {
      roving.onItemBlur(event, messageId);
    }
  });
  return /*#__PURE__*/_jsx(MessageContextProvider, {
    value: ownerState,
    children: /*#__PURE__*/_jsx(Root, _extends({}, composedRootProps, {
      children: children
    }))
  });
});
if (process.env.NODE_ENV !== "production") MessageRoot.displayName = "MessageRoot";